// Maps a Predictus lawsuit `status_slug`/`status_text` to a PrimeVue Tag severity
// so the "Status" column reflects whether a process is active, closed, or paused.

export type LawsuitStatusSeverity = 'info' | 'success' | 'warn' | 'danger' | 'secondary';

export const LAWSUIT_STATUS_LABELS: Record<string, string> & { unknown: string } = {
  archived_final: 'Arquivamento definitivo',
  in_progress: 'Em tramitação',
  archived: 'Arquivamento',
  suspended: 'Suspenso',
  on_appeal: 'Em grau de recurso',
  archived_provisional: 'Arquivamento provisório',
  archived_administrative: 'Arquivado administrativamente',
  unknown: 'Desconhecido',
};

interface StatusRule {
  severity: LawsuitStatusSeverity;
  // Matched against the slug (already snake_case) and the lowercased free text.
  keywords: string[];
}

// Order matters: first matching rule wins.
const STATUS_RULES: StatusRule[] = [
  { severity: 'success', keywords: ['baixad', 'extint', 'encerrad', 'conclu', 'transitad', 'finaliz'] },
  { severity: 'danger', keywords: ['cancel', 'anulad', 'indeferid'] },
  { severity: 'warn', keywords: ['suspens', 'sobrestad', 'susp'] },
  { severity: 'secondary', keywords: ['arquiv'] },
  { severity: 'info', keywords: ['andament', 'ativ', 'tramit', 'distribuid'] },
];

const DEFAULT_SEVERITY: LawsuitStatusSeverity = 'secondary';

export interface LawsuitStatusMeta {
  severity: LawsuitStatusSeverity;
  // Closed/finished processes get a check icon so they stand out from the rest
  // of the list at a glance; other statuses show text only.
  icon: string | null;
}

function severityOf(slug: string | null | undefined, text?: string | null): LawsuitStatusSeverity {
  const haystack = `${slug || ''} ${text || ''}`.toLowerCase();
  if (!haystack.trim()) return DEFAULT_SEVERITY;
  const rule = STATUS_RULES.find((r) => r.keywords.some((kw) => haystack.includes(kw)));
  return rule?.severity ?? DEFAULT_SEVERITY;
}

export function getLawsuitStatusMeta(
  slug: string | null | undefined,
  text?: string | null,
): LawsuitStatusMeta {
  const severity = severityOf(slug, text);
  return { severity, icon: severity === 'success' ? 'task_alt' : null };
}
